/**
 * Patient Consents Tab Component
 *
 * Displays all consent form signatures for a patient at the current clinic.
 * Providers can view signed content, check validity, and see signature history.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileCheck,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Eye,
  ShieldCheck,
  ShieldX,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getPatientConsents, PatientConsentSignature } from '@/services/consentService';
import { useClinic } from '@/contexts/ClinicContext';

interface PatientConsentsTabProps {
  patientId?: string;
  patientName?: string;
}

const PatientConsentsTab: React.FC<PatientConsentsTabProps> = ({
  patientId,
  patientName,
}) => {
  const { clinic } = useClinic();
  const [isLoading, setIsLoading] = useState(true);
  const [consents, setConsents] = useState<PatientConsentSignature[]>([]);
  const [showExpired, setShowExpired] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState<PatientConsentSignature | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  const fetchConsents = useCallback(async () => {
    if (!clinic?.id || !patientId) return;

    setIsLoading(true);
    try {
      const response = await getPatientConsents(clinic.id, patientId, showExpired);
      setConsents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch patient consents:', error);
      setConsents([]);
    } finally {
      setIsLoading(false);
    }
  }, [clinic?.id, patientId, showExpired]);

  useEffect(() => {
    fetchConsents();
  }, [fetchConsents]);

  const handleViewConsent = (consent: PatientConsentSignature) => {
    setSelectedConsent(consent);
    setIsViewOpen(true);
  };

  const getStatusBadge = (consent: PatientConsentSignature) => {
    if (consent.revoked_at) {
      return (
        <Badge variant="destructive" className="gap-1">
          <ShieldX className="h-3 w-3" />
          Revoked
        </Badge>
      );
    }
    if (consent.expires_at && new Date(consent.expires_at) < new Date()) {
      return (
        <Badge variant="secondary" className="gap-1 bg-amber-100 text-amber-700">
          <AlertTriangle className="h-3 w-3" />
          Expired
        </Badge>
      );
    }
    if (consent.is_valid) {
      return (
        <Badge variant="secondary" className="gap-1 bg-emerald-100 text-emerald-700">
          <ShieldCheck className="h-3 w-3" />
          Valid
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="gap-1">
        <XCircle className="h-3 w-3" />
        Invalid
      </Badge>
    );
  };

  const getSignatureTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      drawn: 'Drawn Signature',
      typed: 'Typed Signature',
      checkbox: 'Checkbox Agreement',
    };
    return labels[type] || type;
  };

  const getFormTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      telehealth: 'Telehealth',
      hipaa: 'HIPAA',
      financial: 'Financial',
      treatment: 'Treatment',
      custom: 'Custom',
    };
    return labels[type] || type;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const validCount = consents.filter(
    (c) => c.is_valid && !c.revoked_at && (!c.expires_at || new Date(c.expires_at) > new Date())
  ).length;
  const totalCount = consents.length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-500">Loading consent forms...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-slate-900">Consent Forms</h3>
          <Badge variant="outline" className="gap-1">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
            {validCount} valid
          </Badge>
          {totalCount > validCount && (
            <Badge variant="outline" className="gap-1 text-slate-500">
              {totalCount} total
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowExpired(!showExpired)}
            className="text-xs"
          >
            {showExpired ? 'Hide Expired/Revoked' : 'Show All History'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchConsents}
            className="text-xs"
          >
            <RefreshCw className="mr-1 h-3 w-3" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Empty State */}
      {consents.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FileCheck className="mb-4 h-12 w-12 text-slate-300" />
            <h4 className="text-lg font-medium text-slate-700">No Consent Forms Found</h4>
            <p className="mt-1 text-sm text-slate-500">
              {showExpired
                ? 'This patient has no consent forms on record.'
                : 'No valid consent forms found. Try "Show All History" to see expired or revoked forms.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Consent List */}
      <div className="space-y-3">
        {consents.map((consent) => (
          <Card
            key={consent.id}
            className="transition-all hover:border-slate-300 hover:shadow-sm"
          >
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
                  <FileCheck className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-slate-900">
                      {consent.consentTemplate?.name || 'Consent Form'}
                    </h4>
                    {getStatusBadge(consent)}
                    {consent.consentTemplate?.form_type && (
                      <Badge variant="outline" className="text-xs">
                        {getFormTypeLabel(consent.consentTemplate.form_type)}
                      </Badge>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Signed {formatDate(consent.signed_at)}
                    </span>
                    <span>{getSignatureTypeLabel(consent.signature_type)}</span>
                    <span>v{consent.template_version}</span>
                  </div>
                  {consent.expires_at && (
                    <p className="mt-1 text-xs text-slate-400">
                      Expires: {formatDate(consent.expires_at)}
                    </p>
                  )}
                  {consent.revoked_at && consent.revocation_reason && (
                    <p className="mt-1 text-xs text-red-500">
                      Revocation reason: {consent.revocation_reason}
                    </p>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleViewConsent(consent)}
                className="flex-shrink-0"
              >
                <Eye className="mr-1 h-4 w-4" />
                View
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* View Consent Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5" />
              {selectedConsent?.consentTemplate?.name || 'Consent Form'}
              {selectedConsent && getStatusBadge(selectedConsent)}
            </DialogTitle>
          </DialogHeader>
          {selectedConsent && (
            <div className="space-y-4">
              {/* Metadata */}
              <div className="grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-4 text-sm">
                <div>
                  <span className="font-medium text-slate-600">Signed:</span>{' '}
                  <span className="text-slate-900">{formatDate(selectedConsent.signed_at)}</span>
                </div>
                <div>
                  <span className="font-medium text-slate-600">Signature Type:</span>{' '}
                  <span className="text-slate-900">
                    {getSignatureTypeLabel(selectedConsent.signature_type)}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-slate-600">Version:</span>{' '}
                  <span className="text-slate-900">{selectedConsent.template_version}</span>
                </div>
                <div>
                  <span className="font-medium text-slate-600">Form Type:</span>{' '}
                  <span className="text-slate-900">
                    {getFormTypeLabel(selectedConsent.consentTemplate?.form_type || 'custom')}
                  </span>
                </div>
                {selectedConsent.expires_at && (
                  <div className="col-span-2">
                    <span className="font-medium text-slate-600">Expires:</span>{' '}
                    <span className="text-slate-900">
                      {formatDate(selectedConsent.expires_at)}
                    </span>
                  </div>
                )}
              </div>

              {/* Signed Content */}
              <div>
                <h4 className="mb-2 font-medium text-slate-700">Signed Content</h4>
                <div className="max-h-[40vh] overflow-y-auto rounded-lg border border-slate-200 bg-white p-4">
                  <div
                    className="prose prose-sm max-w-none text-slate-700"
                    dangerouslySetInnerHTML={{ __html: selectedConsent.signed_content }}
                  />
                </div>
              </div>

              {/* Drawn Signature */}
              {selectedConsent.signature_type === 'drawn' &&
                selectedConsent.signature_data?.data && (
                  <div>
                    <h4 className="mb-2 font-medium text-slate-700">Signature</h4>
                    <div className="inline-block rounded-lg border border-slate-200 bg-white p-4">
                      <img
                        src={selectedConsent.signature_data.data}
                        alt="Patient signature"
                        className="max-h-24"
                      />
                    </div>
                  </div>
                )}

              {/* Typed Signature */}
              {selectedConsent.signature_type === 'typed' &&
                selectedConsent.signature_data?.data && (
                  <div>
                    <h4 className="mb-2 font-medium text-slate-700">Typed Signature</h4>
                    <div className="inline-block rounded-lg border border-slate-200 bg-white px-6 py-3">
                      <span className="font-serif text-lg italic text-slate-800">
                        {selectedConsent.signature_data.data}
                      </span>
                    </div>
                  </div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientConsentsTab;
